import { getZrmLeaseInfo, getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';
import PropertyConstants from '../../constants/PropertyConstants';
import { trackEvent } from '../index';

import type { ZrmClickstreamLease, ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

const { PROPERTY_TABLE_TRIGGER_OBJECT_MAP: _PROPERTY_TABLE_TRIGGER_OBJECT_MAP } = PropertyConstants;

export const EVENT_IDS = {
  VIEW_PROPERTIES_PAGE: '6038',
  PROPERTY_CARD_CLICK: '6040',
  PROPERTY_CARD_PAYMENTS: '7101',
  PROPERTY_CARD_LEASES: '7102',
  PROPERTY_CARD_POTENTIAL_TENANTS: '7103',
  PROPERTY_CARD_OVERFLOW_MENU: '7104',
  PROPERTY_LIST_FILTER: '7105',
  PROPERTY_CARD_HOVER: '7119',
  LEASES_CARD_DETAILS_CLICK: '6958',
} as const;

// https://dataportal.zillowgroup.net/event-registry/6038
export const emitViewPropertiesPageEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.VIEW_PROPERTIES_PAGE,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'properties',
      trigger_type_nm: 'view',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'properties',
    },
    semantic: {
      semantic_event_nm: 'view_content',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/6040
export const emitPropertyCardClickEvent = ({
  user,
  currentProperty,
}: {
  user: ZrmClickstreamUser | null;
  currentProperty: ZrmClickstreamListing;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_template_id: '168',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PROPERTY_CARD_CLICK,
      event_type_version_id: '3',
    },
    clickstream_trigger: {
      trigger_location_nm: 'properties',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'no_trigger_object',
      trigger_source_nm: 'property_card_click',
    },
    semantic: {
      semantic_event_nm: 'click_into_property',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7101
export const emitPropertyCardPaymentsEvent = ({
  user,
  currentProperty,
  lease,
  triggerObject,
}: {
  user: ZrmClickstreamUser | null;
  currentProperty: ZrmClickstreamListing;
  lease?: ZrmClickstreamLease;
  triggerObject: (typeof _PROPERTY_TABLE_TRIGGER_OBJECT_MAP.RENT_TRIGGERS)[keyof typeof _PROPERTY_TABLE_TRIGGER_OBJECT_MAP.RENT_TRIGGERS];
}) =>
  trackEvent.leaseListingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PROPERTY_CARD_PAYMENTS,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'properties',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'property_card_payments',
    },
    semantic: {
      semantic_event_nm: 'click_into_property',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
    ...getZrmLeaseInfo(lease),
  });

// https://dataportal.zillowgroup.net/event-registry/7102
export const emitPropertyCardLeasesEvent = ({
  user,
  currentProperty,
  lease,
  triggerObject,
}: {
  user: ZrmClickstreamUser | null;
  currentProperty: ZrmClickstreamListing;
  lease?: ZrmClickstreamLease;
  triggerObject: (typeof _PROPERTY_TABLE_TRIGGER_OBJECT_MAP.LEASE_TRIGGERS)[keyof typeof _PROPERTY_TABLE_TRIGGER_OBJECT_MAP.LEASE_TRIGGERS];
}) =>
  trackEvent.leaseListingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PROPERTY_CARD_LEASES,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'properties',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'property_card_leases',
    },
    semantic: {
      semantic_event_nm: 'click_into_property',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
    ...getZrmLeaseInfo(lease),
  });

// https://dataportal.zillowgroup.net/event-registry/7103
export const emitPropertyCardPotentialTenantsEvent = ({
  user,
  currentProperty,
  lease,
  triggerObject,
}: {
  user: ZrmClickstreamUser | null;
  currentProperty: ZrmClickstreamListing;
  lease?: ZrmClickstreamLease;
  triggerObject: (typeof _PROPERTY_TABLE_TRIGGER_OBJECT_MAP.POTENTIAL_TENANTS_TRIGGERS)[keyof typeof _PROPERTY_TABLE_TRIGGER_OBJECT_MAP.POTENTIAL_TENANTS_TRIGGERS];
}) =>
  trackEvent.leaseListingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PROPERTY_CARD_POTENTIAL_TENANTS,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'properties',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'property_card_potential_tenants',
    },
    semantic: {
      semantic_event_nm: 'click_into_property',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
    ...getZrmLeaseInfo(lease),
  });

// https://dataportal.zillowgroup.net/event-registry/7104
export const emitPropertyCardOverflowMenuEvent = ({
  user,
  currentProperty,
  lease,
}: {
  user: ZrmClickstreamUser | null;
  currentProperty: ZrmClickstreamListing;
  lease?: ZrmClickstreamLease;
}) =>
  trackEvent.leaseListingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PROPERTY_CARD_OVERFLOW_MENU,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'properties',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'overflow_menu',
      trigger_source_nm: 'property_card',
    },
    semantic: {
      semantic_event_nm: 'opens_overflow_menu',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
    ...getZrmLeaseInfo(lease),
  });

// https://dataportal.zillowgroup.net/event-registry/7105
export const emitPropertyListFilterEvent = ({
  user,
  triggerObject,
}: {
  user: ZrmClickstreamUser | null;
  triggerObject: (typeof _PROPERTY_TABLE_TRIGGER_OBJECT_MAP.FILTER_TRIGGERS)[keyof typeof _PROPERTY_TABLE_TRIGGER_OBJECT_MAP.FILTER_TRIGGERS];
}) =>
  trackEvent.user({
    envelope: {
      event_template_id: '167',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PROPERTY_LIST_FILTER,
      event_type_version_id: '2',
    },
    clickstream_trigger: {
      trigger_location_nm: 'properties',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'property_list_filter',
    },
    semantic: {
      semantic_event_nm: 'filter_property_list',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/7119
export const emitPropertyCardHoverEvent = ({
  user,
  currentProperty,
  lease,
  triggerObject,
}: {
  user: ZrmClickstreamUser | null;
  currentProperty: ZrmClickstreamListing;
  lease?: ZrmClickstreamLease;
  triggerObject: (typeof _PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS)[keyof typeof _PROPERTY_TABLE_TRIGGER_OBJECT_MAP.HOVER_TRIGGERS];
}) =>
  trackEvent.leaseListingUser({
    envelope: {
      event_template_id: '177',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.PROPERTY_CARD_HOVER,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'properties',
      trigger_type_nm: 'interaction',
      trigger_object_nm: triggerObject,
      trigger_source_nm: 'property_card_hover',
    },
    semantic: {
      semantic_event_nm: 'property_card_hover',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmUserInfo(user),
    ...getZrmListingInfo(currentProperty),
    ...getZrmLeaseInfo(lease),
  });

// https://dataportal.zillowgroup.net/event-registry/6958
export const emitLeasesCardDetailsClickEvent = ({ user }: { user: ZrmClickstreamUser | null }) =>
  trackEvent.leaseUser({
    envelope: {
      event_template_id: '283',
      event_template_version_id: '1',
      event_type_id: EVENT_IDS.LEASES_CARD_DETAILS_CLICK,
      event_type_version_id: '1',
    },
    clickstream_trigger: {
      trigger_location_nm: 'zrm_property_overview',
      trigger_type_nm: 'interaction',
      trigger_object_nm: 'leases_card',
      trigger_source_nm: 'details',
    },
    semantic: {
      semantic_event_nm: 'see_details',
      topic_tag_txt: [],
    },
    ...getZrmUserInfo(user),
    zrm_lease_info: {
      lease_type_cd: null,
      lease_status_txt: null,
      lease_bundle_id: null,
    },
  });
